import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import type { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import useCallbackRef from "@shared/hooks/useCallbackRef/useCallbackRef";
import Box from "@ui-kit/layout/Box/Box.web";
import { useCallback, useMemo, useRef } from "react";
import { recordAppError } from "@shared/utils/errors/errorLogger";

import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";

import { usePaymentManager } from "../../contexts/PaymentManagerContext";
import {
  WalletElementSharedProps,
  WalletSourceStep,
} from "./WalletElement.props";
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import YunoPaymentGateway from "../../gateways/YunoPaymentGateway/YunoPaymentGateway";

import StripeWalletElement from "../../stripe/StripeWalletElement/StripeWalletElement.web";
import YunoWalletElement from "../yuno/YunoWalletElement/YunoWalletElement.web";

type WalletElementProps = WalletElementSharedProps & {
  sourceStep: WalletSourceStep;
  extraAnalyticsParams?: Record<string, unknown>;
};

type NotShownReason =
  | "no_gateway"
  | "unsupported_gateway"
  | "non_credit_payment"
  | "wallet_not_available"
  | "flag_disabled";

function getNotShownReason(
  walletAvailable: boolean,
  isOneClickEnabled: boolean,
  reason: NotShownReason | null
): NotShownReason | null {
  if (!walletAvailable) return reason;
  if (!isOneClickEnabled) return "flag_disabled";
  return null;
}

/**
 * Dynamically displays a component for virtual wallet payments, such as Apple Pay, Google Pay, etc.
 *
 * Requires the PaymentManagerContext.
 */
function WalletElement({
  sourceStep,
  blockReasons = [],
  onClickBlocked,
  onElementReady,
  onElementError,
  onWalletClick,
  onWalletCancel,
  onAvailablePaymentMethods,
  extraAnalyticsParams,
  ...props
}: WalletElementProps): JSX.Element | null {
  const { manager } = usePaymentManager();
  const { isFlagActive } = useDjangoWaffle();
  const hasTrackedExposure = useRef(false);

  const onElementReadyRef = useCallbackRef(onElementReady);
  const onElementErrorRef = useCallbackRef(onElementError);
  const onClickBlockedRef = useCallbackRef(onClickBlocked);
  const onWalletClickRef = useCallbackRef(onWalletClick);
  const onWalletCancelRef = useCallbackRef(onWalletCancel);

  const isBlocked = blockReasons.length > 0;
  const isBasketUpdating = blockReasons.includes("basket_updating");

  const walletAnalyticsData = useMemo(
    () =>
      manager.basket
        ? {
            basket: manager.basket as IBasket,
            additionalParams: {
              source_step: sourceStep,
              block_reasons: blockReasons,
              ...extraAnalyticsParams,
            },
          }
        : undefined,
    [manager.basket, sourceStep, blockReasons, extraAnalyticsParams]
  );

  const isOneClickEnabled =
    extraAnalyticsParams?.one_click_checkout_enabled !== false;

  const trackExposure = useCallback(
    (walletAvailable: boolean, reason: NotShownReason | null = null) => {
      try {
        if (hasTrackedExposure.current || !walletAnalyticsData) return;
        hasTrackedExposure.current = true;

        const shown = walletAvailable && isOneClickEnabled;

        UserInteractionLogger.walletExposure({
          ...walletAnalyticsData,
          additionalParams: {
            ...walletAnalyticsData.additionalParams,
            wallet_shown: shown,
            wallet_available: walletAvailable,
            not_shown_reason: getNotShownReason(
              walletAvailable,
              isOneClickEnabled,
              reason
            ),
          },
        });
      } catch (error) {
        recordAppError(error);
      }
    },
    [walletAnalyticsData, isOneClickEnabled]
  );

  const handleElementReady = useCallback(() => {
    onElementReadyRef.current?.();
    trackExposure(true);
  }, [trackExposure]);

  const handleElementError = useCallback(() => {
    onElementErrorRef.current?.();
    trackExposure(false, "wallet_not_available");
  }, [trackExposure]);

  const handleOverlayClick = useCallback(() => {
    onClickBlockedRef.current?.();
    if (walletAnalyticsData) {
      UserInteractionLogger.walletSelection(walletAnalyticsData, true);
    }
  }, [walletAnalyticsData]);

  const handleWalletClick = useCallback(() => {
    if (walletAnalyticsData) {
      UserInteractionLogger.walletSelection(walletAnalyticsData, false);
    }
    onWalletClickRef.current?.();
  }, [walletAnalyticsData]);

  const handleWalletCancel = useCallback(() => {
    onWalletCancelRef.current?.();
  }, []);

  if (!manager.handler?.gateway) {
    trackExposure(false, "no_gateway");
    return null;
  }

  if (manager.handler.gateway instanceof StripePaymentGateway) {
    if (
      manager.basket &&
      manager.basket.paymentType !== PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id
    ) {
      trackExposure(false, "non_credit_payment");
      return null;
    }

    return (
      <Box
        position="relative"
        opacity={isBasketUpdating ? 0.5 : 1}
        transition="opacity 0.2s"
      >
        <StripeWalletElement
          {...props}
          onElementReady={handleElementReady}
          onElementError={handleElementError}
          onWalletClick={handleWalletClick}
          onWalletCancel={handleWalletCancel}
          onAvailablePaymentMethods={onAvailablePaymentMethods}
        />
        {isBlocked && (
          <Box
            position="absolute"
            inset="none"
            zIndex={1}
            cursor={isBasketUpdating ? "not-allowed" : "pointer"}
            onClick={handleOverlayClick}
          />
        )}
      </Box>
    );
  }

  if (manager.handler.gateway instanceof YunoPaymentGateway) {
    if (!isFlagActive("enable_yuno_wallet", manager.basket?.paymentVenue)) {
      return null;
    }

    if (
      manager.basket &&
      manager.basket.paymentType !== PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id
    ) {
      trackExposure(false, "non_credit_payment");
      return null;
    }

    return (
      <Box
        position="relative"
        opacity={isBasketUpdating ? 0.5 : 1}
        transition="opacity 0.2s"
      >
        <YunoWalletElement
          {...props}
          paymentVenue={manager.basket?.paymentVenue}
          onElementReady={handleElementReady}
          onElementError={handleElementError}
          onWalletClick={handleWalletClick}
          onWalletCancel={handleWalletCancel}
          onAvailablePaymentMethods={onAvailablePaymentMethods}
        />
        {isBlocked && (
          <Box
            position="absolute"
            inset="none"
            zIndex={1}
            cursor={isBasketUpdating ? "not-allowed" : "pointer"}
            onClick={handleOverlayClick}
          />
        )}
      </Box>
    );
  }

  if (
    !(manager.handler.gateway instanceof StripePaymentGateway) &&
    !(manager.handler.gateway instanceof YunoPaymentGateway)
  ) {
    trackExposure(false, "unsupported_gateway");
  }

  return null;
}

export default WalletElement;
